import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import Button from '@/components/ui/Button';
import InputField from '@/components/common/InputField';
import { Column } from '@/types/columns';
import { toastMessage } from '@/utils/toast-message';
import { fetchLeadColumns, createLead, getLeadDetails, bulkAssignLeads } from '@/actions/lead-manager';
import ColumnsService from '@/utils/columnsCache';
import { optimizedApi } from '@/utils/api/optimized-api';
import { useUsers } from '@/contexts/UsersContext';
import {
    LeadProfileSettings,
    LEAD_SOURCE_SUBCATEGORY_MAP,
    CURRICULUM_OPTIONS,
    ACADEMIC_YEAR_OPTIONS,
    VISTA_DEFAULT_ACADEMIC_YEAR,
    PROFILE_FIELD_LABELS,
} from '@/types/leads/Profile';

// Vista Form clients (Extended Profile active) add leads with Vista Form fields. The native
// Source is still filled — reports and the Yellowslate side read it — from the chosen
// Sub-category; Status starts as "New Lead".
const NATIVE_SOURCE_FOR_SUBCATEGORY: Record<string, string> = {
    'Website': 'Website', 'Meta': 'Facebook', 'Yellowslate': 'Yellowslate', 'Google': 'Google',
    'WhatsApp Bot': 'Whatsapp Bot', 'Community event': 'Community event', 'Pre-school': 'Pre-school',
    'Corporate event': 'Corporate Event', 'Call': 'Phone', 'Email': 'Email', 'Direct walk-in': 'Walkin',
};
const VISTA_CLIENT_ID = 11008;
const normName = (v: string) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

interface VistaAddLeadValues {
    counsellor: string;
    lead_source_category: string;
    lead_source_subcategory: string;
    student_first_name: string;
    student_last_name: string;
    phone: string;
    email: string;
    parent_name: string;
    grade: string;
    curriculum: string;
    academic_year_joining: string;
}
const emptyVistaValues: VistaAddLeadValues = {
    counsellor: '', lead_source_category: '', lead_source_subcategory: '', student_first_name: '',
    student_last_name: '', phone: '', email: '', parent_name: '', grade: '', curriculum: '',
    academic_year_joining: VISTA_DEFAULT_ACADEMIC_YEAR,
};

interface FormValues {
    [key: string]: string | number | null;
}

interface AddLeadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    refreshData?: () => void;
    // Vista Form clients: called with the new lead so the caller can open it on the Vista Form.
    onLeadCreated?: (lead: any) => void;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    refreshData,
    onLeadCreated
}) => {
    const [columns, setColumns] = useState<Column[]>([]);
    const { activeUsers } = useUsers();
    const [vistaMode, setVistaMode] = useState(false);
    const [vistaValues, setVistaValues] = useState<VistaAddLeadValues>(emptyVistaValues);
    const [vistaErrors, setVistaErrors] = useState<Partial<Record<keyof VistaAddLeadValues, string>>>({});
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState<FormValues>({});
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

    // Fetch columns when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchFormColumns();
        }
    }, [isOpen]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFormValues({});
            setFieldErrors({});
            setVistaValues(emptyVistaValues);
            setVistaErrors({});
        }
    }, [isOpen]);

    const fetchFormColumns = async () => {
        try {
            setLoading(true);
            // Only Vista's account checks its Vista Form settings; every other client skips the call
            // and gets the standard form.
            let isVistaAccount = false;
            try {
                const user = JSON.parse(localStorage.getItem('crm_user') || 'null');
                isVistaAccount = (user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk) === VISTA_CLIENT_ID;
            } catch { /* not Vista */ }
            if (isVistaAccount) {
                try {
                    const profileRes = await optimizedApi.get<LeadProfileSettings>('/lead-profile/settings');
                    const settings = profileRes.success ? profileRes.data : null;
                    setVistaMode(!!settings?.is_active && (settings.sections_enabled || []).includes('communication'));
                } catch {
                    setVistaMode(false);
                }
            } else {
                setVistaMode(false);
            }
            let response = await ColumnsService.getColumns(true);

            if (!response.success) {
                response = await fetchLeadColumns();
            }

            if (response.success && response.columns) {
                // Update columns to make specific fields mandatory
                // Created date is not on the form: the server always sets it to its own time
                const activeColumns = response.columns
                    .filter(col => col.col_name !== 'created_date')
                    .map(col => {
                        if (['phone', 'source', 'status'].includes(col.col_name)) {
                            return { ...col, col_required: true };
                        }
                        return col;
                    });
                setColumns(activeColumns);

                // Initialize form values with defaults
                const initialValues: FormValues = {};

                activeColumns.forEach(column => {
                    if (column.col_type === 'select' && column.options.length > 0) {
                        // Set default to first option or "New Lead" status
                        if (column.col_name === 'status') {
                            const newLeadOption = column.options.find(opt =>
                                opt.opt_name.toLowerCase().includes('new') ||
                                opt.opt_name.toLowerCase().includes('lead')
                            );
                            initialValues[column.uuid] = newLeadOption?.uuid || column.options[0]?.uuid || '';
                        } else if (column.col_name === 'academic_year' || column.col_label.toLowerCase().includes('academic year')) {
                            // Set default to "26-27" for academic year
                            const ay2627Option = column.options.find(opt =>
                                opt.opt_name === '26-27' || opt.opt_value === '26-27'
                            );
                            initialValues[column.uuid] = ay2627Option?.uuid || '';
                        } else {
                            const defaultOption = column.options.find(opt => opt.opt_value === 'please_select');
                            initialValues[column.uuid] = defaultOption?.uuid || '';
                        }
                    } else {
                        initialValues[column.uuid] = '';
                    }
                });

                setFormValues(initialValues);
            }
        } catch (error) {
            console.error('Error fetching columns:', error);
            toastMessage('Failed to load form fields', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (uuid: string, value: string | number) => {
        setFormValues(prev => ({
            ...prev,
            [uuid]: value
        }));

        // Clear error for this field
        if (fieldErrors[uuid]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[uuid];
                return newErrors;
            });
        }
    };

    const setVista = (key: keyof VistaAddLeadValues, value: string) => {
        setVistaValues((v) => ({ ...v, [key]: value, ...(key === 'lead_source_category' ? { lead_source_subcategory: '' } : {}) }));
        if (vistaErrors[key]) setVistaErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    };
    const nativeCol = (name: string) => columns.find((c) => c.col_name === name);

    const handleVistaSubmit = async () => {
        const v = vistaValues;
        const errors: Partial<Record<keyof VistaAddLeadValues, string>> = {};
        if (!v.phone.trim()) errors.phone = 'Contact Phone is required';
        if (!v.lead_source_category) errors.lead_source_category = 'Lead Source Category is required';
        if (!v.lead_source_subcategory) errors.lead_source_subcategory = 'Lead Source Sub-category is required';
        if (nativeCol('name')?.col_required && !v.parent_name.trim()) errors.parent_name = 'Contact Name is required';
        if (nativeCol('email')?.col_required && !v.email.trim()) errors.email = 'Contact Email is required';
        if (nativeCol('grade')?.col_required && !v.grade) errors.grade = 'Grade is required';
        if (Object.keys(errors).length > 0) {
            setVistaErrors(errors);
            toastMessage(`Please fill in required fields: ${Object.values(errors).map((m) => String(m).replace(' is required', '')).join(', ')}`, 'error');
            return;
        }

        // Native fields: keep every column's default (e.g. created date), then fill ours.
        const formData: { [key: string]: any } = {};
        columns.forEach((c) => { formData[c.uuid] = formValues[c.uuid] ?? ''; });
        const put = (name: string, value: any) => { const c = nativeCol(name); if (c) formData[c.uuid] = value; };
        put('phone', v.phone.trim());
        put('name', v.parent_name.trim());
        put('email', v.email.trim());
        put('grade', v.grade);
        const sourceCol = nativeCol('source');
        if (sourceCol) {
            const wanted = NATIVE_SOURCE_FOR_SUBCATEGORY[v.lead_source_subcategory] || 'Others';
            const opt = (sourceCol.options || []).find((o: any) => normName(o.opt_name) === normName(wanted))
                || (sourceCol.options || []).find((o: any) => normName(o.opt_name) === 'others');
            formData[sourceCol.uuid] = opt?.uuid || formData[sourceCol.uuid] || '';
        }
        // Status keeps its "New Lead" default from fetchFormColumns.

        try {
            setLoading(true);
            const response: any = await createLead({ formData });
            if (!response.success) {
                toastMessage(response.message || 'Failed to create lead', 'error');
                return;
            }
            const leadUuid: string | undefined = response.data?.leadUuid || response.data?.uuid;
            if (!leadUuid) {
                toastMessage(response.message || 'Lead created', 'success');
                onClose();
                if (refreshData) await refreshData();
                return;
            }

            // Vista Form fields, then the counsellor — each best-effort so the lead still opens.
            const profileRes = await optimizedApi.put(`/lead-profile/${leadUuid}`, {
                lead_source_category: v.lead_source_category,
                lead_source_subcategory: v.lead_source_subcategory,
                student_first_name: v.student_first_name.trim() || null,
                student_last_name: v.student_last_name.trim() || null,
                curriculum: v.curriculum || null,
                academic_year_joining: v.academic_year_joining || VISTA_DEFAULT_ACADEMIC_YEAR,
            });
            if (!profileRes.success) toastMessage('Lead created, but the Vista Form details could not be saved', 'error');
            if (v.counsellor) {
                const assignRes: any = await bulkAssignLeads([leadUuid], v.counsellor, '');
                if (!assignRes?.success) toastMessage('Lead created, but the counsellor could not be assigned', 'error');
            }

            toastMessage('Lead created successfully!', 'success');
            onClose();
            if (refreshData) await refreshData();
            if (onSuccess) onSuccess();
            if (onLeadCreated) {
                const details: any = await getLeadDetails(leadUuid).catch(() => null);
                onLeadCreated(details?.success && details.data ? details.data : { uuid: leadUuid, phone: v.phone, name: v.parent_name, email: v.email });
            }
        } catch (error) {
            console.error('Error creating lead:', error);
            toastMessage(error instanceof Error ? error.message : 'An error occurred while creating the lead', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (vistaMode) {
            await handleVistaSubmit();
            return;
        }

        // Validate required fields
        const requiredFields = columns.filter(col => col.col_required);
        const missingFields = requiredFields.filter(field => {
            const value = formValues[field.uuid];
            return value === null || value === undefined || value === '';
        });

        if (missingFields.length > 0) {
            const newErrors: { [key: string]: string } = {};
            missingFields.forEach(field => {
                newErrors[field.uuid] = `${field.col_label} is required`;
            });
            setFieldErrors(newErrors);
            toastMessage(`Please fill in required fields: ${missingFields.map(f => f.col_label).join(', ')}`, 'error');
            return;
        }

        try {
            setLoading(true);

            const formData: { [key: string]: any } = {};
            columns.forEach(column => {
                const value = formValues[column.uuid];
                formData[column.uuid] = value;
            });

            const createData = { formData };
            const response = await createLead(createData);

            if (response.success) {
                toastMessage(response.message || 'Lead created successfully!', 'success');

                // Reset form
                setFormValues({});
                setFieldErrors({});

                // Close modal
                onClose();

                // Refresh data
                if (refreshData) {
                    await refreshData();
                }

                // Call success callback
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                toastMessage(response.message || 'Failed to create lead', 'error');
            }
        } catch (error) {
            console.error('Error creating lead:', error);
            // Extract error message from API response
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while creating the lead';
            toastMessage(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const mapOptions = (options: any[]) => {
        return options
            .sort((a, b) => a.opt_order - b.opt_order)
            .map(option => ({
                value: option.uuid,
                label: option.opt_name
            }));
    };

    const renderField = (column: Column) => {
        const { uuid, col_label, col_type, col_required, col_description, options } = column;

        const fieldValue = formValues[uuid] !== null && formValues[uuid] !== undefined
            ? String(formValues[uuid])
            : '';

        const commonProps = {
            id: uuid,
            name: uuid,
            required: col_required,
            disabled: loading,
            placeholder: col_description || `Enter ${col_label}`,
            value: fieldValue,
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                handleChange(uuid, e.target.value);
            },
            error: !!fieldErrors[uuid],
            className: 'w-full'
        };

        switch (col_type) {
            case 'select':
                return (
                    <InputField
                        {...commonProps}
                        type="select"
                        options={mapOptions(options)}
                    />
                );
            case 'textarea':
                return (
                    <InputField
                        {...commonProps}
                        type="textarea"
                        rows={3}
                    />
                );
            case 'dateTime':
                return (
                    <InputField
                        {...commonProps}
                        type="datetime-local"
                    />
                );
            case 'date':
                return (
                    <InputField
                        {...commonProps}
                        type="date"
                    />
                );
            case 'email':
                return (
                    <InputField
                        {...commonProps}
                        type="email"
                        placeholder="example@email.com"
                    />
                );
            case 'number':
            case 'numeric':
                return (
                    <InputField
                        {...commonProps}
                        type="number"
                    />
                );
            case 'phone':
            case 'tel':
                return (
                    <InputField
                        {...commonProps}
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                    />
                );
            case 'url':
                return (
                    <InputField
                        {...commonProps}
                        type="text"
                        placeholder="https://example.com"
                    />
                );
            default:
                return (
                    <InputField
                        {...commonProps}
                        type="text"
                    />
                );
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
                onClick={onClose}
            />

            {/* Slide Panel */}
            <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-[51] flex flex-col transform transition-transform duration-300 ease-in-out">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg">
                            <Plus className="h-5 w-5 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900">Add New Lead</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading && columns.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                            <span className="ml-3 text-gray-600">Loading form fields...</span>
                        </div>
                    ) : (
                        vistaMode ? (
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(() => {
                                const gradeCol = nativeCol('grade');
                                const subOpts = (cat: string) => LEAD_SOURCE_SUBCATEGORY_MAP[cat] || [];
                                const field = (key: keyof VistaAddLeadValues, label: string, required: boolean, el: React.ReactNode, wide = false) => (
                                    <div key={key} className={wide ? 'md:col-span-2' : ''}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {label}{required && <span className="text-red-500 ml-1">*</span>}
                                        </label>
                                        {el}
                                        {vistaErrors[key] && <p className="text-xs text-red-500 mt-1">{vistaErrors[key]}</p>}
                                    </div>
                                );
                                const text = (key: keyof VistaAddLeadValues, type = 'text') => (
                                    <InputField id={`vista_${key}`} name={`vista_${key}`} type={type as any} value={vistaValues[key]}
                                        disabled={loading} error={!!vistaErrors[key]} className="w-full"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setVista(key, e.target.value)} />
                                );
                                const select = (key: keyof VistaAddLeadValues, options: { value: string; label: string }[]) => (
                                    <InputField id={`vista_${key}`} name={`vista_${key}`} type="select" value={vistaValues[key]}
                                        disabled={loading} error={!!vistaErrors[key]} className="w-full" options={options}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setVista(key, e.target.value)} />
                                );
                                const asOpts = (vals: string[]) => vals.map((x) => ({ value: x, label: x }));
                                return [
                                    field('counsellor', 'Counsellor Assigned', false, select('counsellor', [{ value: '', label: 'Yet to assign' }, ...activeUsers.map((u) => ({ value: u.uuid, label: u.userName }))]), true),
                                    field('lead_source_category', PROFILE_FIELD_LABELS.lead_source_category, true, select('lead_source_category', asOpts(Object.keys(LEAD_SOURCE_SUBCATEGORY_MAP)))),
                                    field('lead_source_subcategory', PROFILE_FIELD_LABELS.lead_source_subcategory, true, select('lead_source_subcategory', asOpts(subOpts(vistaValues.lead_source_category)))),
                                    field('student_first_name', PROFILE_FIELD_LABELS.student_first_name, false, text('student_first_name')),
                                    field('student_last_name', PROFILE_FIELD_LABELS.student_last_name, false, text('student_last_name')),
                                    field('phone', 'Contact Phone', true, text('phone', 'tel')),
                                    field('email', 'Contact Email', !!nativeCol('email')?.col_required, text('email', 'email')),
                                    field('parent_name', 'Contact Name', !!nativeCol('name')?.col_required, text('parent_name')),
                                    field('grade', 'Grade', !!gradeCol?.col_required, select('grade', gradeCol ? mapOptions(gradeCol.options || []) : [])),
                                    field('curriculum', PROFILE_FIELD_LABELS.curriculum, false, select('curriculum', asOpts(CURRICULUM_OPTIONS))),
                                    field('academic_year_joining', PROFILE_FIELD_LABELS.academic_year_joining, false, select('academic_year_joining', asOpts(ACADEMIC_YEAR_OPTIONS))),
                                ];
                            })()}
                        </form>
                        ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Form Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {columns
                                    .sort((a, b) => a.col_order - b.col_order)
                                    .map((column) => (
                                        <div key={column.uuid} className={`${
                                            column.col_type === 'textarea' ? 'md:col-span-2' : ''
                                        }`}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {column.col_label}
                                                {column.col_required && <span className="text-red-500 ml-1">*</span>}
                                            </label>
                                            {renderField(column)}
                                        </div>
                                    ))}
                            </div>
                        </form>
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || columns.length === 0}
                        className="bg-brandColor text-white hover:bg-brand-hover"
                    >
                        {loading ? 'Creating...' : 'Create Lead'}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default React.memo(AddLeadModal);